import type { FieldNote } from '@/services/estimates-service'
import { parseMapData } from '@/lib/map-data'

// Builds and prints a Project Overview for the field team: all the project info,
// scope, materials, equipment, labor, misc, subcontractors, inclusions/exclusions
// and notes in one document, with the site map on its own page at the end. Like
// the notes print, this is a standalone browser printout — it never touches the
// proposal PDF pipeline or the send-to-client flow, so it can't reach the
// customer. Auto-filled from the proposal + estimate.
//
// PRICING: four sections carry money — All Materials, Haul & Dump,
// Miscellaneous and Subcontractors. Each was added deliberately, on request,
// because the field team dispatches off this sheet: the material spend to order
// against, the trucking/dump split to book haulers, and (Oct 2026) the misc and
// subcontractor costs, which the customer's proposal either hides by default or
// never itemises at all. Labor and Equipment stay scope-only — they read as a
// crew list, not a spend. Keep it that way: no other section should gain a price
// without the same explicit decision, and a test asserts no "$" survives outside
// the four.
//
// These are INTERNAL COSTS, not customer prices: line items carry `rate` and
// `lineTotal` before markup (markup lives in the estimate's markupData), and the
// haul figures are costs too. So this sheet will not add up to the bid total,
// which is intended — it is a dispatch document, never sent to a client.

export interface ProjectOverviewInput {
  proposal: {
    company?: string
    projectTitle?: string
    client?: string
    clientContact?: string
    clientPhone?: string
    projectAddress?: string
    estimatorName?: string
    proposalNumber?: string
    categories?: Array<{ name: string; groups: Array<{ items: string[] }> }>
    inclusions?: string[]
    exclusions?: string[]
  }
  estimate: {
    title?: string
    isUnitPricing?: boolean
    supervisorName?: string
    supervisorPhone?: string
    isPublicWorks?: boolean
    // `rate`/`lineTotal` are the item's INTERNAL cost, pre-markup. Only the
    // materials table prints them — see the PRICING note above.
    lineItems?: Array<{ description: string; quantity: number; unit: string; category: string; section?: string | null; hideFromProposal?: boolean; hoursPerDay?: number; daysPerWeek?: number; rate?: number; lineTotal?: number }>
    subcontractorsData?: unknown
    lchData?: unknown
    mapJson?: unknown
  }
  notes: FieldNote[]
  assets: Array<{ url: string; category: string; sortOrder?: number; createdAt: string }>
}

const SECTION_LABELS: Record<string, string> = {
  'asphalt': 'Asphalt', 'asphalt-rnr': 'Asphalt R&R', 'asphalt-overlay': 'Asphalt Overlay',
  'asphalt-grading': 'Grading', 'concrete': 'Concrete', 'concrete-grading': 'Concrete Grading',
  'grading': 'Grading', 'seal-coat': 'Seal Coat', 'striping': 'Striping', 'misc': 'Miscellaneous',
}
function sectionLabel(s?: string | null): string {
  if (!s) return '—'
  return SECTION_LABELS[s] || (s.charAt(0).toUpperCase() + s.slice(1))
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, c => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string))
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function fmtNum(n: number): string {
  return (Math.round(n * 100) / 100).toLocaleString('en-US')
}
/** Only All Materials and Haul & Dump print money — see the PRICING note in the header. */
function fmtMoney(n: number): string {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const blank = '<span class="blank">__________</span>'

/** Haul & Dump (LCH) rows. Same array-or-JSON-string shape as subcontractorsData.
 * `parseLchData` in section-totals.ts does this too, but its SectionLchRow type
 * declares only what section totals need, so this sheet carries its own. */
interface LchPrintRow {
  name?: string;
  section?: string | null;
  area?: number;
  depth?: number;
  length?: number;
  /** How trucking is priced; absent on rows bid before crew pricing ('clh'). */
  pricing?: 'clh' | 'crew';
  lchTime?: number;
  trucks?: number;
  days?: number;
  hoursPerDay?: number;
  tons?: number;
  loads?: number;
  /** Combined trucking + dump total. */
  totalHaulDumpCost?: number;
  /** The two halves behind that total, both already computed and persisted by the
   * Haul & Dump tab (loads x trucking $/hr x CLH, and loads x cost per dump).
   * Printed split out so the crew can book haulers and dump separately. */
  totalTruckingCost?: number;
  totalDumpCost?: number;
}
/**
 * The haul plan for one row, in the crew's terms. A crew-priced row books trucks
 * for days (with the hours spelled out only when it isn't a standard 8-hour day);
 * an older row still carries haul hours per load.
 */
function haulPlan(r: LchPrintRow): string {
  if (r.pricing === 'crew') {
    const trucks = r.trucks ?? 0;
    const days = r.days ?? 0;
    if (trucks <= 0 || days <= 0) return '—';
    const hours = r.hoursPerDay ?? 8;
    return `${fmtNum(trucks)} trk × ${fmtNum(days)} d` + (hours !== 8 ? ` @ ${fmtNum(hours)} hr` : '');
  }
  return (r.lchTime ?? 0) > 0 ? fmtNum(r.lchTime as number) + ' hr' : '—';
}

function parseLchRows(raw: unknown): LchPrintRow[] {
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? (() => { try { const p = JSON.parse(raw || '[]'); return Array.isArray(p) ? p : [] } catch { return [] } })()
      : [];
  return arr as LchPrintRow[];
}

/** `subcontractorsData` arrives as an array from Prisma and as a JSON string from
 * other paths; scope-only here, so cost/markup/bonds are deliberately ignored. */
function parseSubRows(raw: unknown): Array<{ company?: string; description?: string; serviceType?: string; section?: string | null; cost?: number; bonds?: number }> {
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? (() => { try { const p = JSON.parse(raw || '[]'); return Array.isArray(p) ? p : [] } catch { return [] } })()
      : []
  return arr as Array<{ company?: string; description?: string; serviceType?: string; section?: string | null; cost?: number; bonds?: number }>
}

export function printProjectOverview(input: ProjectOverviewInput): void {
  const { proposal, estimate, notes, assets } = input

  const isUnitPricing = !!estimate.isUnitPricing

  // EVERY line item, including the ones kept off the customer's proposal.
  //
  // This sheet used to apply the proposal's own visibility rule, which read
  // backwards on an internal document — most of all for Miscellaneous, where
  // items are hidden BY DEFAULT and the estimator opts each one into the
  // proposal. So the costs least likely to reach the customer were also the ones
  // missing from the crew's sheet. Requested, Oct 2026: the proposal checkbox
  // governs the customer's copy; the Project Overview always shows the whole job.
  const visibleLi = estimate.lineItems || []
  const unitPriceLi = visibleLi.filter(li => li.section === 'unit-price')

  // Scope of work by service.
  //
  // On a UNIT PRICE bid the writeup is not the scope — the unit price schedule is.
  // proposal.categories is still populated from whatever is left in the writeup, so
  // printing it put services on the sheet that are not being done (an asphalt and
  // seal coat scope on a concrete-only unit price bid). The customer-facing proposal
  // already drops scope in this mode ("stripped-down proposal … no scope" in
  // TemplateRenderer); the crew sheet now does the same and lists the unit price
  // items instead, so the field team still gets the work — quantities only, no
  // pricing, like the rest of this document.
  const scope = isUnitPricing
    ? (unitPriceLi.length
        ? [{
            service: 'Unit Price',
            lines: unitPriceLi.map(li =>
              [li.description, [fmtNum(li.quantity), li.unit].filter(Boolean).join(' ')]
                .filter(Boolean).join(' — '),
            ),
          }]
        : [])
    : (proposal.categories || [])
        .map(c => ({ service: c.name, lines: (c.groups || []).flatMap(g => g.items || []).filter(Boolean) }))
        .filter(s => s.lines.length > 0)
  const isEquip = (c: string) => c === 'equipment'
  const isLabor = (c: string) => c === 'labor' || c === 'crew'
  const isMisc = (c: string) => c === 'misc'

  // Materials/misc show quantity + unit. "All Materials" is a catch-all — every
  // line item that isn't equipment/labor/misc (materials, striping…).
  // `rate`/`total` are carried for every item but only rendered by the materials
  // table — Miscellaneous stays scope-only. See the PRICING note in the header.
  const toItem = (li: (typeof visibleLi)[number]) => ({
    name: li.description, service: sectionLabel(li.section), qty: li.quantity, unit: li.unit,
    rate: li.rate ?? 0, total: li.lineTotal ?? 0,
  })
  // Equipment/labor read as a COUNT, not hours: `quantity` IS the machine count /
  // headcount (the estimate UI labels labor "Men"); hours live in hoursPerDay ×
  // daysPerWeek. Printing them through a qty table made "2 laborers" read "2 hr".
  const toResource = (li: { description: string; quantity: number; section?: string | null; hoursPerDay?: number; daysPerWeek?: number }) => {
    const parts: string[] = []
    if (li.hoursPerDay) parts.push(`${fmtNum(li.hoursPerDay)} hr/day`)
    if (li.daysPerWeek) parts.push(`${fmtNum(li.daysPerWeek)} day${li.daysPerWeek > 1 ? 's' : ''}`)
    return { name: li.description, service: sectionLabel(li.section), count: fmtNum(li.quantity), schedule: parts.join(' × ') }
  }
  // Unit price rows are the scope above; drop them here or they print twice
  // (they carry category 'misc', so they were landing under "Miscellaneous Items").
  const bucketLi = isUnitPricing ? visibleLi.filter(li => li.section !== 'unit-price') : visibleLi
  const materials = bucketLi.filter(li => !isEquip(li.category) && !isLabor(li.category) && !isMisc(li.category)).map(toItem)
  const materialsTotal = materials.reduce((sum, m) => sum + m.total, 0)
  const equipment = bucketLi.filter(li => isEquip(li.category)).map(toResource)
  const labor = bucketLi.filter(li => isLabor(li.category)).map(toResource)
  const misc = bucketLi.filter(li => isMisc(li.category)).map(toItem)
  const miscTotal = misc.reduce((sum, m) => sum + m.total, 0)

  const inclusions = (proposal.inclusions || []).filter(Boolean)
  const exclusions = (proposal.exclusions || []).filter(Boolean)

  // Subcontractors — who, what scope, and what they cost. The customer never sees
  // these: their cost is folded into the service's price on the proposal and the
  // companies are never itemised. The crew dispatching off this sheet needs the
  // whole picture, so the quote and bonds print here. Requested, Oct 2026.
  const subcontractors = parseSubRows(estimate.subcontractorsData)
    .filter(s => s.company || s.description)
    // Current entries are tied to the service tab they were made in; legacy ones
    // carry a free-text Service Type instead.
    .map(s => ({
      name: s.company || '—',
      scope: s.description || '',
      type: s.section ? sectionLabel(s.section) : (s.serviceType || ''),
      // Cost and bonds are what the subcontractor charges, before any markup —
      // the same pre-markup basis as every other figure on this sheet.
      cost: Number(s.cost) || 0,
      bonds: Number(s.bonds) || 0,
    }))
  const subsTotal = subcontractors.reduce((sum, s) => sum + s.cost + s.bonds, 0)

  // Haul & Dump (LCH) — what has to be hauled off, how many truck loads that is,
  // and what the hauling costs.
  //
  // THE ONLY PRICED SECTION ON THIS SHEET (see the PRICING note in the header).
  // The figure is `totalHaulDumpCost`, the combined trucking + dump total already
  // computed on the estimate's Haul & Dump tab. The rate breakdown behind it
  // (trucking $/hr, cost per dump, and the two subtotals) stays on that tab —
  // printing all five would take this table to eleven columns on a portrait page.
  const haulDump = parseLchRows(estimate.lchData)
    .filter(r => (r.tons ?? 0) > 0 || (r.loads ?? 0) > 0 || (r.area ?? 0) > 0 || (r.length ?? 0) > 0)
    .map(r => {
      // Curb-type rows are measured by length; everything else by area x depth.
      const dims = (r.length ?? 0) > 0
        ? fmtNum(r.length as number) + ' LF'
        : (r.area ?? 0) > 0
          ? fmtNum(r.area as number) + ' sq ft' + ((r.depth ?? 0) > 0 ? ' @ ' + fmtNum(r.depth as number) + ' in' : '')
          : '—';
      return {
        name: r.name || '—',
        service: sectionLabel(r.section),
        dims,
        // What the crew needs to book: trucks and days for a crew-priced row, the
        // per-load haul time for a row bid the original way.
        clh: haulPlan(r),
        tons: r.tons ?? 0,
        loads: r.loads ?? 0,
        trucking: r.totalTruckingCost ?? 0,
        dump: r.totalDumpCost ?? 0,
        cost: r.totalHaulDumpCost ?? 0,
      };
    });
  const haulTotals = haulDump.reduce(
    (a, r) => ({
      tons: a.tons + r.tons,
      loads: a.loads + r.loads,
      trucking: a.trucking + r.trucking,
      dump: a.dump + r.dump,
      cost: a.cost + r.cost,
    }),
    { tons: 0, loads: 0, trucking: 0, dump: 0, cost: 0 },
  );

  // Drawn measurements from the map (SF for areas, LF for lines).
  let measurements: Array<{ label: string; measure: string }> = []
  try {
    const parsed = parseMapData(estimate.mapJson as any)
    measurements = (parsed.shapes || []).map(s => ({
      label: s.type === 'subtract' ? `${s.label} (cut-out)` : s.label,
      measure: `${s.type === 'subtract' ? '−' : ''}${fmtNum(s.measurement)} ${s.type === 'line' ? 'LF' : 'SF'}`,
    }))
  } catch { /* no/invalid map data */ }

  // Latest saved map image.
  const mapAsset = (assets || [])
    .filter(a => a.category === 'map')
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.createdAt).localeCompare(String(b.createdAt)))
    .pop()

  const textNotes = (notes || []).filter(n => !n.photoAssetId)

  // ---- Build sections of HTML ----
  const scopeRows = scope.length
    ? scope.map(s => `<tr><td class="svc">${esc(s.service)}</td><td><ul>${s.lines.map(l => `<li>${esc(l)}</li>`).join('')}</ul></td></tr>`).join('')
    : `<tr><td colspan="2" class="blank">No scope lines on this proposal.</td></tr>`


  // Materials and Miscellaneous print cost so the field team can order against the
  // sheet and check a delivery ticket. Rate is the item's unit cost, Total its
  // extended cost — both pre-markup — and the subtotal closes the table.
  const pricedItemTable = (
    items: Array<{ name: string; service: string; qty: number; unit: string; rate: number; total: number }>,
    subtotal: number,
    totalLabel: string,
  ) =>
    items.length
      ? `<table><thead><tr><th>Item</th><th style="width:110px">Service</th><th class="qty" style="width:100px">Qty</th><th class="qty" style="width:85px">Rate</th><th class="qty" style="width:95px">Total</th></tr></thead><tbody>
          ${items.map(i => `<tr><td>${esc(i.name)}</td><td>${esc(i.service)}</td><td class="qty">${fmtNum(i.qty)} ${esc(i.unit)}</td><td class="qty">${fmtMoney(i.rate)}</td><td class="qty">${fmtMoney(i.total)}</td></tr>`).join('')}
          <tr><td colspan="4" style="text-align:right;font-weight:600">${esc(totalLabel)}</td><td class="qty" style="font-weight:600">${fmtMoney(subtotal)}</td></tr>
        </tbody></table>`
      : `<div class="blank">None listed.</div>`

  const resourceTable = (rows: Array<{ name: string; service: string; count: string; schedule: string }>) =>
    rows.length
      ? `<table><thead><tr><th>Item</th><th style="width:120px">Service</th><th class="qty" style="width:70px">Count</th><th style="width:150px">Schedule</th></tr></thead><tbody>
          ${rows.map(r => `<tr><td>${esc(r.name)}</td><td>${esc(r.service)}</td><td class="qty">${esc(r.count)}</td><td>${r.schedule ? esc(r.schedule) : '—'}</td></tr>`).join('')}
        </tbody></table>`
      : `<div class="blank">None listed.</div>`

  // A totals row matters here: the crew wants the overall truck count and haul
  // spend, not just per row. Trucking and Dump are split out from the combined
  // Haul Cost so haulers and the dump can be booked separately — both halves are
  // already persisted on the row, so nothing is recomputed here. Column widths are
  // tight on purpose: nine columns is the most this portrait page will carry.
  const haulTable = haulDump.length
    ? `<table><thead><tr><th>Item</th><th style="width:85px">Service</th><th style="width:115px">Dimensions</th><th class="qty" style="width:76px">Haul</th><th class="qty" style="width:48px">Tons</th><th class="qty" style="width:48px">Loads</th><th class="qty" style="width:80px">Trucking</th><th class="qty" style="width:75px">Dump</th><th class="qty" style="width:85px">Haul Cost</th></tr></thead><tbody>
        ${haulDump.map(r => `<tr><td>${esc(r.name)}</td><td>${esc(r.service)}</td><td>${esc(r.dims)}</td><td class="qty">${esc(r.clh)}</td><td class="qty">${fmtNum(r.tons)}</td><td class="qty">${fmtNum(r.loads)}</td><td class="qty">${fmtMoney(r.trucking)}</td><td class="qty">${fmtMoney(r.dump)}</td><td class="qty">${fmtMoney(r.cost)}</td></tr>`).join('')}
        <tr><td colspan="4" style="text-align:right;font-weight:600">Total</td><td class="qty" style="font-weight:600">${fmtNum(haulTotals.tons)}</td><td class="qty" style="font-weight:600">${fmtNum(haulTotals.loads)}</td><td class="qty" style="font-weight:600">${fmtMoney(haulTotals.trucking)}</td><td class="qty" style="font-weight:600">${fmtMoney(haulTotals.dump)}</td><td class="qty" style="font-weight:600">${fmtMoney(haulTotals.cost)}</td></tr>
      </tbody></table>`
    : `<div class="blank">None listed.</div>`

  // Quote and bonds are what the subcontractor charges; Total is the two added,
  // which is the figure that carries into the bid before markup.
  const subsTable = subcontractors.length
    ? `<table><thead><tr><th style="width:200px">Subcontractor</th><th>Scope of work</th><th class="qty" style="width:90px">Quote</th><th class="qty" style="width:80px">Bonds</th><th class="qty" style="width:95px">Total</th></tr></thead><tbody>
        ${subcontractors.map(s => `<tr><td>${esc(s.name)}${s.type ? `<div class="submeta">${esc(s.type)}</div>` : ''}</td><td>${s.scope ? esc(s.scope) : '<span class="blank">—</span>'}</td><td class="qty">${fmtMoney(s.cost)}</td><td class="qty">${fmtMoney(s.bonds)}</td><td class="qty">${fmtMoney(s.cost + s.bonds)}</td></tr>`).join('')}
        <tr><td colspan="4" style="text-align:right;font-weight:600">Subcontractor total</td><td class="qty" style="font-weight:600">${fmtMoney(subsTotal)}</td></tr>
      </tbody></table>`
    : `<div class="blank">None listed.</div>`

  const bulletList = (arr: string[]) =>
    arr.length ? `<ul class="bullets">${arr.map(x => `<li>${esc(x)}</li>`).join('')}</ul>` : `<div class="blank">None listed.</div>`

  const measureRows = measurements.length
    ? measurements.map(m => `<div class="measrow"><span>${esc(m.label)}</span><span class="m">${esc(m.measure)}</span></div>`).join('')
    : `<tr><td colspan="2" class="blank">No measurements drawn.</td></tr>`

  // Page box for the map, in inches against the @page letter/portrait box above:
  // 8.5x11 less .5in margins = 7.5 x 10.0 usable, less the heading (~.43in) and the
  // footer (~.45in, which lands on this last page) = 9.1in of height for the map.
  //
  // Measurements share the page, so give them back only what they actually need:
  // they render in 3 columns at ~.23in a row, so the strip grows a row at a time
  // instead of costing a flat guess. Capped so a very long list cannot squeeze the
  // map away — past the cap it simply flows onto another page.
  const MAP_BOX_W = '7.5in'
  const MEAS_ROWS = Math.ceil(measurements.length / 3)
  const measReserveIn = measurements.length ? Math.min(0.15 + MEAS_ROWS * 0.23, 2.6) : 0
  const MAP_BOX_H = `${Math.max(9.1 - measReserveIn, 4).toFixed(2)}in`

  const mapCell = mapAsset
    ? `<div class="mapbox" style="--boxw:${MAP_BOX_W};--boxh:${MAP_BOX_H}">
        <img id="sitemap" src="${esc(mapAsset.url)}" alt="Site map" onerror="this.parentNode.innerHTML='<div class=&quot;maptray&quot;>Map image unavailable</div>'" />
      </div>`
    : `<div class="maptray">No saved map image</div>`

  const notesBlock = textNotes.length
    ? textNotes.map(n => `<div class="note"><div class="nmeta"><span>${esc(n.authorName || 'Staff')}</span><span>${fmtDate(n.createdAt)}</span></div><div class="ntext">${esc(n.text)}</div></div>`).join('')
    : `<div class="blank">No notes.</div>`

  const project = esc(proposal.projectTitle || estimate.title || 'Project')
  const isStr = (x: string | undefined): x is string => Boolean(x)
  const contact = [proposal.clientContact, proposal.clientPhone].filter(isStr).map(esc).join(' · ') || blank
  const supervisor = [estimate.supervisorName, estimate.supervisorPhone].filter(isStr).map(esc).join(' · ') || blank
  const publicWorks = estimate.isPublicWorks == null
    ? blank
    : (estimate.isPublicWorks ? 'YES — prevailing wage applies' : 'No')
  const company = proposal.company ? esc(proposal.company) : ''

  const win = window.open('', '_blank', 'width=900,height=1000')
  if (!win) { alert('Please allow pop-ups for this site to print the project overview.'); return }

  win.document.write(`<!doctype html><html><head><meta charset="utf-8">
    <title>Project Overview — ${project}</title>
    <style>
      :root{--ink:#1e293b;--mut:#64748b;--line:#cbd5e1;--line2:#e2e8f0;--hd:#0f172a}
      *{box-sizing:border-box}
      body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;color:var(--ink);margin:26px 30px;font-size:13px;line-height:1.45}
      .topbar{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid var(--hd);padding-bottom:10px;margin-bottom:8px}
      .brand{font-size:13px;font-weight:700;color:var(--hd);letter-spacing:.05em;text-transform:uppercase;margin-bottom:3px}
      h1{font-size:22px;margin:0;letter-spacing:.02em}
      .sub{color:var(--mut);font-size:12px;margin-top:2px}
      .tag{background:#111827;color:#fff;font-size:11px;font-weight:700;letter-spacing:.06em;padding:6px 12px;border-radius:5px;text-transform:uppercase;white-space:nowrap}
      h2{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:var(--hd);border-bottom:1.5px solid var(--line);padding-bottom:4px;margin:18px 0 8px}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:3px 26px}
      .f{display:flex;gap:8px;padding:3px 0;border-bottom:1px dotted var(--line2)}
      .f .k{color:var(--mut);min-width:132px;font-size:11.5px}
      .f .v{font-weight:600;flex:1}
      .blank{color:#94a3b8;font-weight:400;font-style:italic}
      table{width:100%;border-collapse:collapse;margin-top:4px}
      th,td{text-align:left;padding:6px 8px;border:1px solid var(--line2);font-size:12px;vertical-align:top}
      th{background:#f1f5f9;font-size:10.5px;text-transform:uppercase;letter-spacing:.03em;color:var(--hd)}
      td.qty{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
      td.svc{font-weight:600;width:120px}
      .submeta{font-size:10.5px;color:var(--mut);margin-top:2px}
      ul{margin:0;padding-left:18px}
      li{margin:1px 0}
      ul.bullets{padding-left:20px}
      ul.bullets li{margin:2px 0}
      .incl-excl{display:grid;grid-template-columns:1fr 1fr;gap:22px;align-items:start}
      .map-page{page-break-before:always}
      /* Full-page site map.
         The map gets a page of its own, kept PORTRAIT. Site maps come off the map
         panel in landscape, so drawn upright on a portrait page they were limited by
         page WIDTH and printed small. Rotating a landscape map 90deg lets its long
         edge run down the page instead, so it prints far larger at the same aspect
         ratio. Only max-* constraints are set and width/height stay auto, so the
         image always scales proportionally — it is never stretched.
         A rotated element still occupies its PRE-rotation box, so the two axes swap:
         once rotated, the image's own width runs down the page and its height runs
         across it. --boxw/--boxh are therefore swapped for the rotated case. */
      .mapbox{position:relative;width:var(--boxw);height:var(--boxh);margin:0 auto}
      .mapbox img{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
        max-width:var(--boxw);max-height:var(--boxh);border:1px solid var(--line2);border-radius:8px}
      .mapbox img.rot{transform:translate(-50%,-50%) rotate(90deg);
        max-width:var(--boxh);max-height:var(--boxw)}
      /* Measurements under the map: compact multi-column so they cost little height. */
      .measgrid{columns:3;column-gap:22px;margin-top:10px;font-size:11.5px}
      .measrow{display:flex;justify-content:space-between;gap:10px;break-inside:avoid;
        padding:2px 0;border-bottom:1px dotted var(--line2)}
      .measrow .m{font-variant-numeric:tabular-nums;white-space:nowrap;font-weight:600}
      .maptray{border:1px dashed var(--line);border-radius:8px;height:150px;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:12px;background:#f8fafc}
      .note{border:1px solid var(--line2);border-radius:8px;padding:9px 12px;margin-bottom:8px;page-break-inside:avoid}
      .nmeta{display:flex;justify-content:space-between;gap:12px;font-size:11px;color:var(--mut);margin-bottom:4px}
      .nmeta span:first-child{font-weight:600;color:#334155}
      .ntext{font-size:13px;line-height:1.5;white-space:pre-wrap}
      .foot{margin-top:22px;border-top:1px solid var(--line2);padding-top:8px;font-size:10.5px;color:#94a3b8;display:flex;justify-content:space-between}
      @page{size:letter portrait;margin:.5in}
      @media print{body{margin:0}.tag,th{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style>
    <script>
      var printed=false;function doPrint(){if(printed)return;printed=true;try{window.print()}catch(e){}}
      // Rotate the site map only when it actually gains size: a landscape image on a
      // portrait page. Rotating an already-portrait map would make it SMALLER, so the
      // decision needs the real pixel dimensions and can only be made once loaded.
      function orientMap(){
        var i=document.getElementById('sitemap');
        if(!i)return;
        if(i.naturalWidth>i.naturalHeight)i.classList.add('rot');
      }
      window.addEventListener('load',function(){orientMap();setTimeout(doPrint,250)});
      setTimeout(function(){orientMap();doPrint()},1500);
    </script>
    </head><body>
      <div class="topbar">
        <div>${company ? `<div class="brand">${company}</div>` : ''}<h1>PROJECT OVERVIEW</h1><div class="sub">For the field team — internal, not for the customer</div></div>
        <div class="tag">Internal Copy</div>
      </div>

      <h2>Project Details</h2>
      <div class="grid">
        <div class="f"><span class="k">Project Name</span><span class="v">${project}</span></div>
        <div class="f"><span class="k">Proposal Number</span><span class="v">${proposal.proposalNumber ? esc(proposal.proposalNumber) : blank}</span></div>
        <div class="f"><span class="k">Site Address</span><span class="v">${proposal.projectAddress ? esc(proposal.projectAddress) : blank}</span></div>
        <div class="f"><span class="k">Customer</span><span class="v">${proposal.client ? esc(proposal.client) : blank}</span></div>
        <div class="f"><span class="k">Customer Site Contact</span><span class="v">${contact}</span></div>
        <div class="f"><span class="k">Estimator</span><span class="v">${proposal.estimatorName ? esc(proposal.estimatorName) : blank}</span></div>
        <div class="f"><span class="k">Supervisor</span><span class="v">${supervisor}</span></div>
        <div class="f"><span class="k">Public Works</span><span class="v">${publicWorks}</span></div>
      </div>

      <h2>Scope of Work</h2>
      <table><thead><tr><th style="width:120px">Service</th><th>What to do</th></tr></thead><tbody>${scopeRows}</tbody></table>

      <h2>All Materials</h2>
      ${pricedItemTable(materials, materialsTotal, 'Materials total')}

      <h2>Haul &amp; Dump</h2>
      ${haulTable}

      <h2>Equipment &amp; Information</h2>
      ${resourceTable(equipment)}

      <h2>Labor</h2>
      ${resourceTable(labor)}

      <h2>Miscellaneous Items</h2>
      ${pricedItemTable(misc, miscTotal, 'Miscellaneous total')}

      <h2>Subcontractors</h2>
      ${subsTable}

      <h2>Exclusions &amp; Inclusions</h2>
      <div class="incl-excl">
        <div><div class="brand" style="font-size:11px;margin-bottom:4px">Exclusions</div>${bulletList(exclusions)}</div>
        <div><div class="brand" style="font-size:11px;margin-bottom:4px">Inclusions</div>${bulletList(inclusions)}</div>
      </div>

      <h2>Proposal Notes</h2>
      ${notesBlock}

      <div class="map-page">
        <h2>Project Map${measurements.length ? ' &amp; Measurements' : ''}</h2>
        ${mapCell}
        ${measurements.length ? `<div class="measgrid">${measureRows}</div>` : ''}
      </div>

      <div class="foot"><span>${company ? company + ' — ' : ''}Project Overview</span><span>INTERNAL — do not give to customer</span></div>
    </body></html>`)
  win.document.close()
  win.focus()
}
